import {
  getCanSubmitToGroupGallery,
  getCanSubmitToGroupPosts,
  getCanCreateGroupGalleryFolder,
} from '@wix/da-shared-react/pkg/utils/groupPrivs';
import { flagEnabled } from '@wix/da-shared-react/pkg/redux/flags/selectors';
import { AppState } from '../../types/store';
import { PagePriv } from '../../types/pagePrivileges';
import { ModuleType } from '../../types/modules';
import {
  getIsGroupProfile,
  getProfileOwnerExtendedGroup,
  isCurrentUserProfileOwner,
} from './users';
import { getIsGroupFounder, getIsGroupMember, getGroupApps } from './groups';
import { canEditModule } from './modules';

export const getPagePrivileges = state => state.pagePrivileges || {};
export const getCanPerformPageAction = (state, action: string) => {
  const pagePrivs = getPagePrivileges(state);
  return action in pagePrivs && pagePrivs?.[action] === true;
};

export const isUserProfileOwner = (state: AppState) =>
  !getIsGroupProfile(state) && isCurrentUserProfileOwner(state);

export const getCanSubmitToGallery = (state: AppState) =>
  isUserProfileOwner(state) ||
  (getIsGroupProfile(state) &&
    getCanSubmitToGroupGallery(getProfileOwnerExtendedGroup(state)));

export const getCanSubmitToPosts = (state: AppState) =>
  isUserProfileOwner(state) ||
  (getIsGroupProfile(state) &&
    getCanSubmitToGroupPosts(getProfileOwnerExtendedGroup(state)));

export const getCanCreateGalleryFolder = (state: AppState) =>
  isUserProfileOwner(state) ||
  (getIsGroupProfile(state) &&
    getCanCreateGroupGalleryFolder(getProfileOwnerExtendedGroup(state)));

export const getCanJoinGroup = state => !!getGroupApps(state)?.join?.canJoin;

export const getCanLeaveGroup = state => !!getGroupApps(state)?.join?.canLeave;

export const getCanChangeFounder = state => !!getIsGroupFounder(state);

export const getCanManageRoles = state =>
  !!getGroupApps(state)?.roster?.canManage;

export const getCanInviteToGroup = state =>
  !!getCanPerformPageAction(state, PagePriv.CAN_INVITE);

export const getCanCloseGroup = state =>
  !!getGroupApps(state)?.roster?.canClose;

export const getCanRespondToNotifications = state =>
  getCanPerformPageAction(state, PagePriv.CAN_RESPOND_TO_GROUP_NOTIFICATIONS);

export const getCanPin = state =>
  canEditModule(state, ModuleType.POSTS_FEED) ||
  canEditModule(state, ModuleType.FEED);

export const getCanFeature = state =>
  canEditModule(state, ModuleType.FEATURED_POSTS);

export const getCanMessageAdmins = state => getIsGroupMember(state);

export const getCanCustomizePage = (state: AppState) =>
  canEditModule(state, ModuleType.COVER_DEVIATION);

export const getCanChangeAvatar = (state: AppState) =>
  getCanPerformPageAction(state, PagePriv.CAN_CHANGE_AVATAR);

export const getCanRemovePost = state =>
  getIsGroupProfile(state) &&
  flagEnabled(state, 'eclipsed_groups') &&
  canEditModule(state, ModuleType.POSTS_FEED);
